import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Loader2, Phone, MessageCircle, Search } from "lucide-react";
import { DashShell, StatCard, Badge } from "@/components/dash/DashShell";
import { Input } from "@/components/ui/input";
import { api, inr } from "@/lib/api";
import { Users, UserPlus, PhoneCall, Clock, CheckCircle2, TrendingUp } from "lucide-react";

const NAV = [{ to: "/bde", label: "My Leads", icon: LayoutDashboard }];

export default function BdeDashboard() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [filter, setFilter] = useState("");
    const [q, setQ] = useState("");

    const load = useCallback(() => {
        const params = {};
        if (filter) params.lead_status = filter;
        if (q) params.q = q;
        api.get("/bde/leads", { params }).then(({ data }) => setLeads(data));
    }, [filter, q]);

    useEffect(() => { api.get("/bde/stats").then(({ data }) => setStats(data)); }, []);
    useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

    return (
        <DashShell nav={NAV} title="My Dashboard">
            {!stats ? <div className="grid place-items-center py-32 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="My Leads" value={stats.my_leads} icon={Users} testid="bde-stat-leads" />
                        <StatCard label="Today's Follow-ups" value={stats.today_followups} icon={Clock} accent="gold" />
                        <StatCard label="New" value={stats.new_leads} icon={UserPlus} accent="teal" />
                        <StatCard label="Contacted" value={stats.contacted} icon={PhoneCall} accent="gold" />
                        <StatCard label="Pending" value={stats.pending} icon={Clock} accent="red" />
                        <StatCard label="Converted" value={stats.converted} icon={TrendingUp} accent="green" />
                        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} accent="green" />
                    </div>

                    <div className="bg-white rounded-2xl border border-black/5 p-4 shadow-sm flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-teal-900/40" /><Input className="pl-9" placeholder="Search my leads" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bde-search" /></div>
                        <select className="h-9 rounded-md border border-input bg-white px-3 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="bde-filter">
                            <option value="">All</option>{["Assigned", "Contacted", "Requirement Discussion", "Proposal Sent", "Paid", "In Progress", "Completed", "On Hold"].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {leads.map((l) => (
                            <div key={l.lead_id} className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm" data-testid={`bde-lead-${l.lead_id}`}>
                                <div className="flex justify-between items-start">
                                    <div><div className="font-semibold text-teal-900">{l.customer_name}</div><div className="text-xs text-teal-900/50">{l.business_name || "—"} · {l.city || "—"}</div></div>
                                    <Badge status={l.lead_status} />
                                </div>
                                <div className="mt-3 text-sm text-teal-900/70">{l.package || l.service || "—"}</div>
                                <div className="mt-1 font-mono font-bold text-teal-900">{l.amount ? inr(l.amount) : "Enquiry"}</div>
                                {l.next_followup && <div className="text-xs text-gold-amber mt-2">Follow-up: {l.next_followup}</div>}
                                <div className="flex gap-2 mt-4">
                                    <a href={`tel:${l.mobile}`} className="flex-1 border border-teal-900/15 rounded-full py-2 text-sm text-teal-900 flex items-center justify-center gap-1.5 hover:bg-teal-900/5" data-testid={`bde-call-${l.lead_id}`}><Phone className="w-4 h-4" /> Call</a>
                                    <a href={`https://wa.me/91${(l.whatsapp || l.mobile || "").replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noreferrer" className="flex-1 border border-teal-900/15 rounded-full py-2 text-sm text-teal-900 flex items-center justify-center gap-1.5 hover:bg-teal-900/5"><MessageCircle className="w-4 h-4 text-growth" /> Chat</a>
                                    <button onClick={() => nav(`/bde/leads/${l.lead_id}`)} className="btn-gold rounded-full px-4 py-2 text-sm" data-testid={`bde-manage-${l.lead_id}`}>Manage</button>
                                </div>
                            </div>
                        ))}
                        {!leads.length && <p className="text-teal-900/40 text-sm col-span-full text-center py-10">No leads assigned yet.</p>}
                    </div>
                </div>
            )}
        </DashShell>
    );
}
